import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { installApiLanguageHeader } from './apiLanguageHeader';
import {
  APP_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_STORAGE_KEY,
  readLocaleCookie,
  readLocaleFromUrl,
  normalizeLocale,
  writeLocaleCookie,
  appendLocaleToUrl,
} from './locales';

const NAMESPACES = [
  'common',
  'nav',
  'auth',
  'settings',
  'modules',
  'dashboard',
  'analytics',
  'billing',
  'onboarding',
  'errors',
  'help',
  'partner',
  'dataHub',
  'okr',
];

function loadLocaleBundle(locale) {
  const ctx = require.context('../locales', true, /\.json$/);
  const resources = {};
  for (const ns of NAMESPACES) {
    try {
      resources[ns] = ctx(`./${locale}/${ns}.json`);
    } catch {
      if (locale !== DEFAULT_LOCALE) {
        resources[ns] = ctx(`./${DEFAULT_LOCALE}/${ns}.json`);
      }
    }
  }
  return resources;
}

const resources = Object.fromEntries(
  APP_LOCALES.map((locale) => [locale, loadLocaleBundle(locale)]),
);

const customDetector = {
  name: 'xphLocale',
  lookup() {
    const fromUrl = readLocaleFromUrl();
    if (fromUrl) return fromUrl;
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
      if (stored) return normalizeLocale(stored);
    } catch {
      /* ignore */
    }
    const fromCookie = readLocaleCookie();
    if (fromCookie) return fromCookie;
    return undefined;
  },
  cacheUserLanguage(lng) {
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, normalizeLocale(lng));
    } catch {
      /* ignore */
    }
    writeLocaleCookie(normalizeLocale(lng));
    if (window.desktopAPI?.setLocale) {
      window.desktopAPI.setLocale(normalizeLocale(lng)).catch(() => {});
    }
  },
};

const detector = new LanguageDetector();
detector.addDetector(customDetector);

i18n
  .use(detector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: APP_LOCALES,
    defaultNS: 'common',
    ns: NAMESPACES,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    detection: {
      order: ['xphLocale', 'navigator'],
      caches: ['xphLocale'],
    },
  });

document.documentElement.lang = (i18n.language || 'en').split('-')[0];
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng.split('-')[0];
});

// Send the UI locale with every API request so AI replies match the user's language.
installApiLanguageHeader();

export function changeAppLanguage(locale) {
  const next = normalizeLocale(locale);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
  } catch {
    /* ignore */
  }
  writeLocaleCookie(next);
  document.documentElement.lang = next;
  if (window.desktopAPI?.setLocale) {
    window.desktopAPI.setLocale(next).catch(() => {});
  }
  const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
  if (token || document.cookie) {
    const api = process.env.REACT_APP_API_URL || 'https://api.xpherium.com/api';
    fetch(`${api}/auth/language-preference`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'X-App-Language': next,
      },
      body: JSON.stringify({ language: next }),
    }).catch(() => {});
  }
  return i18n.changeLanguage(next);
}

export default i18n;
